import React from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';

interface MobileCardField {
    label: string;
    value: React.ReactNode;
    className?: string;
}

interface MobileCardProps {
    title: string;
    subtitle?: string;
    fields?: MobileCardField[];
    badge?: React.ReactNode;
    icon?: React.ReactNode;
    actions?: React.ReactNode;
    onClick?: () => void;
    className?: string;
    children?: React.ReactNode;
}

/**
 * MobileCard - A card component designed to replace table rows on mobile.
 * 
 * Use this component to convert horizontal table data into a vertical card layout
 * that works well on mobile devices.
 * 
 * Features:
 * - Title with optional subtitle
 * - Badge indicator (status, category, etc.)
 * - Icon on the left
 * - Key-value field pairs
 * - Action buttons in footer
 * - Clickable with chevron indicator
 */
export const MobileCard: React.FC<MobileCardProps> = ({
    title,
    subtitle,
    fields,
    badge,
    icon,
    actions,
    onClick,
    className,
    children,
}) => {
    const Component = onClick ? 'button' : 'div';

    return (
        <Component
            onClick={onClick}
            className={cn(
                "w-full bg-card border rounded-lg p-3 text-left",
                "transition-colors duration-150",
                onClick && "hover:bg-accent/50 active:bg-accent cursor-pointer",
                className
            )}
        >
            {/* Header Row */}
            <div className="flex items-start gap-3">
                {/* Icon */}
                {icon && (
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                        {icon}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Title Row */}
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <h3 className="font-medium text-sm truncate">{title}</h3>
                            {subtitle && (
                                <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {badge}
                            {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                        </div>
                    </div>

                    {/* Fields Grid */}
                    {fields && fields.length > 0 && (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                            {fields.map((field, index) => (
                                <div key={index} className={cn("min-w-0", field.className)}>
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                        {field.label}
                                    </span>
                                    <p className="text-xs font-medium truncate">{field.value}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Children */}
                    {children}
                </div>
            </div>

            {/* Actions Row */}
            {actions && (
                <div className="flex items-center gap-2 mt-3 pt-2 border-t">
                    {actions}
                </div>
            )}
        </Component>
    );
};

/**
 * MobileCardList - A wrapper for displaying MobileCard items
 */
interface MobileCardListProps {
    children: React.ReactNode;
    className?: string;
    emptyMessage?: string;
}

export const MobileCardList: React.FC<MobileCardListProps> = ({
    children,
    className,
    emptyMessage = "No items",
}) => {
    const hasChildren = React.Children.count(children) > 0;

    if (!hasChildren) {
        return (
            <div className="py-8 text-center text-muted-foreground text-sm">
                {emptyMessage}
            </div>
        );
    }

    return (
        <div className={cn("space-y-2", className)}>
            {children}
        </div>
    );
};

export default MobileCard;
